// App-wide translations. Keys are the English text itself, so anything without a
// translation simply shows in English.
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import hi from "./hi";
import mr from "./mr";
import ta from "./ta";
import te from "./te";
import kn from "./kn";
import ml from "./ml";

export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "mr", label: "Marathi" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "kn", label: "Kannada" },
  { code: "ml", label: "Malayalam" },
];

const DICTIONARIES = { hi, mr, ta, te, kn, ml };
const STORAGE_KEY = "app_language";

const readSavedLanguage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return LANGUAGES.some((l) => l.code === saved) ? saved : "en";
  } catch {
    return "en";
  }
};

let currentLanguage = readSavedLanguage();

// Usable outside components too (e.g. toast messages). "{name}" placeholders are filled from vars.
export const translate = (key, vars) => {
  if (typeof key !== "string") return key;
  const dictionary = DICTIONARIES[currentLanguage];
  let text = (dictionary && dictionary[key]) || key;
  if (vars) {
    text = text.replace(/\{(\w+)\}/g, (match, name) => (vars[name] !== undefined ? vars[name] : match));
  }
  return text;
};

const LanguageContext = createContext({ lang: currentLanguage, setLang: () => {}, t: translate });

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(currentLanguage);

  const setLang = useCallback((code) => {
    currentLanguage = code;
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // Private mode etc.: the choice just won't survive a reload
    }
    setLangState(code);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // New function per language so every screen re-renders when it changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const t = useCallback((key, vars) => translate(key, vars), [lang]);

  return <LanguageContext.Provider value={{ lang, setLang, t }}>{children}</LanguageContext.Provider>;
};

export const useT = () => useContext(LanguageContext);
